import { FunctionComponent, ReactElement, ChangeEvent, useState, useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import FormDialog from '@/components/ui/form-dialog';
import { CustomerDevice } from '@/core/entities';
import { Roles } from '@/core/types';
import TextField from '@/components/ui/form-inputs/TextField';
import SelectField from '@/components/ui/form-inputs/SelectField';
import { DEVICE_FORM_KEY } from '@/shared/constants/form-keys';
import { useCreateDevice, useUpdateDevice } from '@/queries/devices';
import { AdminDeviceResponse, CreateDeviceRequest, DeviceType } from '@/api/devices/types';
import useToastMessage from '@/shared/context/toast-message-context';
import { pushToDataLayer, ContentGroup } from '@/shared/utils/analytics';
import { MutationRequest } from '@/api/types';
import useAuth from '@/shared/context/auth-context';

export interface DeviceFormModel {
	name: string;
	serialNumber: string;
	siteId: string;
	deviceType: DeviceType;
	maintenanceDate: string | null;
	deviceId?: string;
}

const initialDeviceFormModel: DeviceFormModel = {
	name: '',
	serialNumber: '',
	siteId: '',
	deviceType: 'space-1',
	maintenanceDate: null,
};

interface DeviceFormProps {
	open: boolean;
	onClose: () => void;
	device?: CustomerDevice;
	onSubmitted?: () => Promise<void> | void;
	siteOptions: { value: string; label: string }[];
	deviceOptions: { value: DeviceType; label: string }[];
	customerName?: string;
}

const DeviceForm: FunctionComponent<DeviceFormProps> = ({
	open,
	onClose,
	device,
	onSubmitted,
	siteOptions,
	customerName,
	deviceOptions = [
		{ value: 'space-1', label: 'Space-1' },
		{ value: 'nova', label: 'Nova' },
	],
}): ReactElement => {
	const { dispatch } = useToastMessage();
	const { user } = useAuth();
	const [formModel, setFormModel] = useState<DeviceFormModel>(initialDeviceFormModel);
	const canEditMaintenanceDate = user?.role === Roles.ACCOUNT_ADMIN;
	const previousMaintenanceDate = useMemo(
		() => (device?.maintenanceDate ? dayjs(device.maintenanceDate) : null),
		[device?.maintenanceDate]
	);

	useEffect(() => {
		if (device) {
			setFormModel({
				name: device.name,
				serialNumber: device.serialNumber,
				siteId: device.site.id,
				deviceId: device.deviceId,
				deviceType: device.deviceType,
				maintenanceDate: device.maintenanceDate,
			});
		} else {
			setFormModel(initialDeviceFormModel);
		}
	}, [device]);

	const onFormClose = () => {
		setFormModel(initialDeviceFormModel);
		onClose();
	};

	const trackCreateDevice = () => {
		pushToDataLayer({
			event: 'device_created',
			content_group: ContentGroup.Customers,
			customer_name: customerName,
		});
	};

	const trackEditDevice = () => {
		pushToDataLayer({
			event: 'device_edited',
			content_group: ContentGroup.Customers,
			customer_name: customerName,
		});
	};

	const { isLoading: isCreateDeviceFormSubmitting, mutate: onCreateDeviceFormSubmit } =
		useCreateDevice({
			onSuccess: async (data: AdminDeviceResponse) => {
				await onSubmitted?.();
				onFormClose();
				dispatch({
					type: 'success',
					content: (
						<>
							<p>Device successfully created</p>
							<p>
								Portal Sync ID:{' '}
								<Box component="span" sx={{ fontFamily: 'Monospace' }}>
									{data.deviceId}
								</Box>
							</p>
							<p>
								Device Key:{' '}
								<Box component="span" sx={{ fontFamily: 'Monospace' }}>
									{data.deviceKey}
								</Box>
							</p>
						</>
					),
					showCloseButton: true,
					disableSelfClosing: true,
				});
				trackCreateDevice();
			},
		});

	const { isLoading: isUpdateDeviceFormSubmitting, mutate: onUpdateDeviceFormSubmit } =
		useUpdateDevice({
			onSuccess: async () => {
				await onSubmitted?.();
				onFormClose();
				dispatch({
					type: 'success',
					content: 'Device successfully updated',
				});
				trackEditDevice();
			},
		});

	const onSubmit = () => {
		const { name, serialNumber, siteId, deviceType, maintenanceDate } = formModel;
		const request: MutationRequest<CreateDeviceRequest> = {
			request: {
				name,
				serialNumber,
				siteId,
				deviceType,
				...(canEditMaintenanceDate && { maintenanceDate }),
			},
			formKey: DEVICE_FORM_KEY,
		};
		if (device) {
			onUpdateDeviceFormSubmit({ id: device.id, request });
		} else {
			onCreateDeviceFormSubmit(request);
		}
	};

	const title = device ? 'Edit device' : 'Create new device';
	const submitLabel = device ? 'Save' : 'Create device';
	const maintenanceDate = formModel.maintenanceDate ? dayjs(formModel.maintenanceDate) : null;
	const isMaintenanceDateInvalid = Boolean(
		canEditMaintenanceDate &&
			previousMaintenanceDate &&
			(!maintenanceDate || maintenanceDate.isBefore(previousMaintenanceDate, 'day'))
	);

	const isFormDisabled: boolean = useMemo(() => {
		const { name, serialNumber, siteId } = formModel;
		const isDisabled =
			!name?.length || !serialNumber?.length || !siteId?.length || isMaintenanceDateInvalid;

		return isDisabled;
	}, [formModel, isMaintenanceDateInvalid]);

	return (
		<FormDialog
			formKey={DEVICE_FORM_KEY}
			title={title}
			open={open}
			onClose={onFormClose}
			fullWidth
			maxWidth="xs"
			actions={{
				cancel: {
					label: 'Cancel',
				},
				confirm: {
					label: submitLabel,
					disabled: isFormDisabled,
					isLoading: isCreateDeviceFormSubmitting || isUpdateDeviceFormSubmitting,
				},
			}}
			onSubmit={onSubmit}
		>
			<Stack direction="column" spacing={2}>
				{device && (
					<TextField
						id="deviceId"
						name="deviceId"
						label="Portal Sync id"
						formKey={DEVICE_FORM_KEY}
						disabled
						fullWidth
						value={device.deviceId ?? ''}
						sx={{
							'.MuiInputBase-input': {
								fontFamily: 'Monospace',
							},
						}}
					/>
				)}
				<TextField
					id="deviceName"
					name="deviceName"
					label="Device name"
					formKey={DEVICE_FORM_KEY}
					required
					fullWidth
					value={formModel.name}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setFormModel({ ...formModel, name: event.target.value })
					}
				/>
				<TextField
					id="serialNumber"
					name="serialNumber"
					label="Serial number"
					formKey={DEVICE_FORM_KEY}
					required
					fullWidth
					value={formModel.serialNumber}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setFormModel({ ...formModel, serialNumber: event.target.value })
					}
				/>
				<SelectField
					id="deviceType"
					name="deviceType"
					label="Device type"
					formKey={DEVICE_FORM_KEY}
					required
					disabled={!!formModel.deviceId}
					fullWidth
					value={formModel.deviceType ?? ''}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setFormModel({ ...formModel, deviceType: event.target.value as DeviceType })
					}
					options={deviceOptions}
				/>
				<SelectField
					id="site"
					name="site"
					label="Assign to site"
					formKey={DEVICE_FORM_KEY}
					required
					fullWidth
					value={formModel.siteId ?? ''}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						setFormModel({ ...formModel, siteId: event.target.value })
					}
					options={siteOptions}
				/>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<DatePicker
						label="Maintenance date"
						value={maintenanceDate}
						onChange={(value) =>
							setFormModel({
								...formModel,
								maintenanceDate: value?.isValid() ? value.format('YYYY-MM-DD') : null,
							})
						}
						minDate={previousMaintenanceDate ?? undefined}
						disabled={!canEditMaintenanceDate}
						slotProps={{
							textField: {
								variant: 'filled',
								fullWidth: true,
								error: isMaintenanceDateInvalid,
								helperText: isMaintenanceDateInvalid
									? 'Maintenance date cannot be older than the previous maintenance date'
									: undefined,
							},
						}}
					/>
				</LocalizationProvider>
			</Stack>
		</FormDialog>
	);
};

export default DeviceForm;
